import _ from 'lodash'
import Guard from 'arg-guard'

import ApiData from './api-data'

import Logger from '@/logger'
import Store from '@/store'

import { ACTIONS } from '@/store/actions'
import { MUTATIONS } from '@/store/mutations'

const loggingCategory = 'Improvements Plan Builder (Metrics)'

/**
 * Evaluates the provided improvements against savings to get the changed savings for any accepted improvements.fa-tooth
 *
 * @param {Array} improvements The improvements to evaluate.
 * @returns {Object} An object containing the metrics data that has changed and data to manage partial results.
 */
const getMetricsChangeSet = function(improvements) {
    const savings = Store.state.improvements.savings

    if (!savings || !savings.length) return []
    const selected = improvements.filter((item) => item.accepted).map((item) => item.id)
    if (!selected.length) return []

    const retVal = savings.find(
        (item) => _.isEmpty(_.xor(selected,
            item.notAppliedImprovementIds && item.notAppliedImprovementIds.length
                ? [].concat(item.improvementIds, item.notAppliedImprovementIds)
                : item.improvementIds)))

    if (!retVal)
        throw new Error(`Cannot locate a savings combination for the selected improvements ${ selected.join(', ') }`)

    if (retVal.notAppliedImprovementIds && retVal.notAppliedImprovementIds.length) {
        Logger.warn(`Partial savings combination used for the selected improvements ${ selected.join(', ') }. Improvement${ retVal.notAppliedImprovementIds.length > 1
            ? `s ${ retVal.notAppliedImprovementIds.join(', ') } were`
            : ` ${ retVal.notAppliedImprovementIds[0] } was` } ignored.`)
    }

    return {
        results: retVal.results,
        partialResult: retVal.notAppliedImprovementIds != null && retVal.notAppliedImprovementIds.length > 0,
        notAppliedIds: retVal.notAppliedImprovementIds,
    }
}

/**
 * Determines if the savings in the VUEX store already contain savings combination data for the provided Initiative Groups or not.
 *
 * @param {Array} improvementIds The IDs of the initiative groups to check for.
 * @returns {boolean}
 */
const storeSavingsContainsInitiativeCombination = function(improvementIds) {
    const savings = Store.state.improvements.savings

    if (!savings || !savings.length) return false
    let filteredStateData = savings.filter(item => item.improvementIds.length == improvementIds.length)
    if (!filteredStateData.length) return false

    for (let i = 0; i < improvementIds.length; i++) {
        if (filteredStateData.length) {
            const id = improvementIds[i]
            filteredStateData = filteredStateData.filter(item => item.improvementIds.includes(id))
        }
    }

    return Boolean(filteredStateData.length && filteredStateData.length > 0)
}

/*
 * The Plan Builder Manager Savings module is a subset of methods to manage metrics.
 */
const PlanBuilderSavings = {

    /**
     * Loads the initial savings into the VUEX store.
     *
     * @returns {Promise<boolean>} A flag, indicating success.
     */
    async loadInitialSavingsIntoStore() {

        const savings = Store.state.improvements.savings
        if (savings && savings.length) {
            Logger.logObject(savings, 'The savings data has already been initialised', loggingCategory)
            return true
        }

        try {
            Logger.log('Loading initial savings data into store asynchronously', loggingCategory)
            await Store.dispatch(ACTIONS.IMPROVEMENTS.appendSavingsDataAsync, { dataLoader: ApiData.loadInitialSavingsAsync })
            return true
        } catch (err) {
            Logger.log(`Unable to load initial savings: ${ err.message }`, loggingCategory)
            throw err
        }
    },

    /**
     * Loads a selection of savings combinations into state.
     * The data will not reload if already present unless 'force' is specified. It takes an address ID and an array of arrays of improvement IDs.
     *
     * @param {Array} improvementIds The IDs of the Initiative Groups to load combinations for.
     * @param {boolean} force An optional parameter to force a load over existing data.
     * @returns {Promise<boolean>} A flag, indicating success.
     */
    async loadInitiativeCombinationSavingsIntoStore(improvementIds, force = false) {
        Guard.throwIf(improvementIds, 'improvementIds')

        if (!force && storeSavingsContainsInitiativeCombination(improvementIds))
            return true

        try {
            await Store.dispatch(ACTIONS.IMPROVEMENTS.appendSavingsDataAsync, {
                dataLoader: ApiData.loadSavingsForInitiativeGroupSetAsync,
                improvementIds,
            })
            return true
        } catch (err) {
            const failedIds = improvementIds.sort((a, b) => a - b)
            Logger.log(`Unable to load savings for improvements (${ failedIds.join(', ') }): ${ err.message }`, loggingCategory)
            throw err
        }
    },

    /**
     * Tries loading the initial savings into the VUEX store, warning if it fails.
     *
     * @returns {Promise<boolean>} A flag, indicating success.
     */
    async tryLoadingInitialSavings() {
        try {
            return await this.loadInitialSavingsIntoStore()
        } catch {
            Logger.warn('Unable to load pre-cached savings', loggingCategory)
            return false
        }
    },

    /**
     * Tries updating the savings metrics in the VUEX store.
     *
     * @returns {Object} An object, indicating success, whether the result is a partial one and any improvement IDs that were not applied.
     */
    async tryUpdatingMetricsAsync() {
        try {
            return await this.updateMetricsAsync()
        } catch (err) {
            Logger.warn(`Unable to update savings metrics for project: ${ err.message }`, loggingCategory)

            return {
                succeeded: false,
                partial: false,
                notApplied: [],
            }
        }
    },

    /**
     * Updates the savings metrics in the VUEX store.
     *
     * @returns {Object} An object, indicating success, whether the result is a partial one and any improvements that were not applied.
     */
    async updateMetricsAsync() {
        Store.commit(MUTATIONS.METRICS.setLoading)

        const currentProject = Store.state.improvements.currentProject
        const clonedImprovements = _.cloneDeep(currentProject.improvements)
        Logger.logObject({ currentProject, clonedImprovements }, 'Cloning Improvements', 'Update metrics Calls')
        const allSelectedIds = clonedImprovements.filter(item => item.accepted).map(item => item.id)
        Logger.logObject({ allSelectedIds }, 'IDs', 'Update metrics Calls')

        try {
            Logger.logObject({ allSelectedIds }, 'Updating Savings Metrics in State', loggingCategory)
            let changes = []
            if (allSelectedIds && allSelectedIds.length) {
                await PlanBuilderSavings.loadInitiativeCombinationSavingsIntoStore(allSelectedIds)
                changes = getMetricsChangeSet(clonedImprovements ? clonedImprovements : [])
            }
            Store.dispatch(ACTIONS.IMPROVEMENTS.updateMetricsFromSavings, changes.results)

            const result = {
                succeeded: true,
                partial: changes.partialResult,
                notApplied: changes.notAppliedIds
                    ? changes.notAppliedIds.map(notApplied => clonedImprovements.find(imp => imp.id == notApplied))
                    : [],
            }

            if (result.partial)
                Store.commit(MUTATIONS.METRICS.updateNotApplied, result.notApplied)
            else
                Store.commit(MUTATIONS.METRICS.clearNotApplied)


            Logger.logObject({ changes: changes.results, result }, 'Metrics Changes', 'Update metrics Calls')
            return result
        } catch (err) {
            Store.commit(MUTATIONS.METRICS.setLoading, false)
            Logger.log(`Unable to update savings metrics: ${ err.message }`, loggingCategory)
            throw err
        }
    },
}

export default PlanBuilderSavings
